import {
  FINISHED_TO_ORDER,
  LOWER_LEG_SLIM_SHORT_SKU,
  LOWER_LEG_SLIM_LONG_SKU,
  LEG
} from '@consts/index'
import { isMatchingVariantForOptions } from './isMatchingVariantForOptions'
import { getValueFromObject } from '@helpers/utils'
import {
  ProductOption,
  ProductVariant,
  ProductWithVariants
} from 'types/product'
import { EmptyObject } from 'types/helpers'
import { FilterOption } from 'types/filters'
import { SelectedOptions } from '@components/product/BackInStockModal/types'

export function checkIfLowerLegRulesApply(filterOptions: FilterOption[] = []) {
  return filterOptions?.some(
    filter => filter.type === 'leg' && Number(filter.value) >= 33
  )
}

export function getAllAvailableProductOptionValues(
  product: ProductWithVariants | EmptyObject = {},
  filterOptions: FilterOption[] = [],
  nameOfFilter = ''
) {
  const { variants, options } = product || {}
  const allLegValues: string[] =
    options?.find((opt: ProductOption) => opt.type === LEG.toLowerCase())
      ?.values ?? []

  const productOptionsList = variants
    ? variants.reduce(
        (
          gatherVariant: Omit<ProductOption, 'name' | 'position'>[],
          variant
        ) => {
          const newGatherVariant = [...gatherVariant]

          let keepVariant = true

          const isFTOVariant = variant.options?.some(
            opt => opt.value === FINISHED_TO_ORDER
          )

          variant.options?.forEach(
            ({ type: optionType, value: optionValue }) => {
              const shouldFilterOutFTOVariants =
                filterOptions?.findIndex(filter => {
                  return (
                    filter.type === optionType &&
                    filter.value.toLowerCase() !==
                      optionValue.trim().toLowerCase() &&
                    FINISHED_TO_ORDER !== optionValue
                  )
                }) > -1

              const selectedLeg = getValueFromOptions(filterOptions, LEG)

              if (
                nameOfFilter === LEG.toLowerCase() &&
                selectedLeg &&
                !isFTOVariant
              ) {
                if (
                  +selectedLeg < 32 &&
                  variant.sku?.endsWith(LOWER_LEG_SLIM_LONG_SKU)
                ) {
                  keepVariant = false
                }
                if (
                  +selectedLeg >= 32 &&
                  variant.sku?.endsWith(LOWER_LEG_SLIM_SHORT_SKU)
                ) {
                  keepVariant = false
                }
              }

              if (nameOfFilter !== optionType && shouldFilterOutFTOVariants) {
                keepVariant = false
              }

              const index = gatherVariant.findIndex(v => v.type === optionType)

              if (index === -1) {
                newGatherVariant.push({
                  type: optionType,
                  values: [optionValue]
                })
              } else {
                newGatherVariant[index] = {
                  ...newGatherVariant[index],
                  values: [
                    ...new Set([...newGatherVariant[index].values, optionValue])
                  ]
                }
              }
            }
          )

          // FTO variants on taper products cover only a subset of leg sizes
          // based on their SKU suffix (03 = short legs < 32, 04 = long legs
          // >= 32). Replace the catch-all 'Finished to Order' leg value with
          // the specific leg sizes this variant actually covers so that leg
          // availability correctly reflects stock before a leg is selected.
          if (
            nameOfFilter === LEG.toLowerCase() &&
            isFTOVariant &&
            keepVariant
          ) {
            const legIdx = newGatherVariant.findIndex(
              v => v.type === LEG.toLowerCase()
            )
            if (legIdx > -1) {
              const coveredLegs = variant.sku?.endsWith(
                LOWER_LEG_SLIM_SHORT_SKU
              )
                ? allLegValues.filter(v => Number(v) < 32)
                : variant.sku?.endsWith(LOWER_LEG_SLIM_LONG_SKU)
                ? allLegValues.filter(v => Number(v) >= 32)
                : [FINISHED_TO_ORDER]

              newGatherVariant[legIdx] = {
                ...newGatherVariant[legIdx],
                values: [
                  ...new Set([
                    ...newGatherVariant[legIdx].values.filter(
                      v => v !== FINISHED_TO_ORDER
                    ),
                    ...coveredLegs
                  ])
                ]
              }
            }
          }

          const hasStock = variant.inventory_quantity > 0

          return keepVariant && hasStock ? newGatherVariant : gatherVariant
        },
        []
      )
    : []

  return productOptionsList
}

export function filterOutSomeLowerLegVariants<T extends { sku: string }>(
  variants: T[] = [],
  selectedLegLength?: string
) {
  return variants.filter(variant => {
    if (selectedLegLength) {
      if (
        +selectedLegLength < 32 &&
        variant.sku.endsWith(LOWER_LEG_SLIM_LONG_SKU)
      ) {
        return false
      }

      if (
        +selectedLegLength >= 32 &&
        variant.sku.endsWith(LOWER_LEG_SLIM_SHORT_SKU)
      ) {
        return false
      }
    }

    return true
  })
}

function getValueFromOptions(filterOptions: FilterOption[], name: string) {
  return filterOptions.find(
    option => option.type.toLowerCase() === name.toLowerCase()
  )?.value
}

export function getVariantItem(
  product: ProductWithVariants | EmptyObject = {},
  filterOptions: FilterOption[] = []
) {
  const productOptions = getValueFromObject<ProductOption[]>(
    product,
    'options',
    []
  )
  const productVariants = getValueFromObject<ProductVariant[]>(
    product,
    'variants',
    []
  )
  const selectedLegLength = getValueFromOptions(filterOptions, LEG)

  const filteredVariants =
    productVariants.filter(
      variant =>
        variant.inventory_quantity > 0 &&
        isMatchingVariantForOptions({ productOptions, variant, filterOptions })
    ) || []

  // A leg can match both its finished SKU and the Finished to Order SKU. TAL
  // fabric keeps the finished SKU in stock, so prefer whichever Torque holds
  // rather than sending the order to TAL.
  return filterOutSomeLowerLegVariants(
    filteredVariants,
    selectedLegLength
  ).sort(
    (first, second) => Number(isAtTorque(second)) - Number(isAtTorque(first))
  )
}

function isAtTorque(variant: ProductVariant) {
  return variant.fulfillment_location === 'Torque'
}

export function findVariantForOptions(
  variants: ProductVariant[] | undefined,
  options: SelectedOptions
): ProductVariant | undefined {
  return variants?.find(variant =>
    variant.options.every(
      ({ type, value }) =>
        value === FINISHED_TO_ORDER || String(options[type]) === value
    )
  )
}

export { getFiltersOptionsState } from './getFiltersOptionsState'
